import utils from "byzantine/src/utils";
import { useLocalization } from "@fluent/react";
import { entities, useFeature } from "byzantine";
import { translateTopMenuItems } from "byzantine/src/l10n/SettingLocalizers";
import { InstitutionFeatureFluentIds } from "byzantine/src/l10n/SettingFluentIds";
import type { RenderItemMenuProps } from "byzantine/src/types/Institution.types";
import { memo } from "react";

import Details from "../../Details";
import { isAppItem, isLinkItem, isNafItem, isAoiobItem } from "../utils";
import LinkItem from "./linkItem.component";
import AppItem from "./appItem.component";
import PositivePayItem from "./PositivePayItem.component";
import AOIOBItem from "./aoiobItem.component";
import { LegacyNafUrl } from "../../../types";

type Props = {
  item: RenderItemMenuProps<API.NestedMenu>;
  legacyNafUrls: LegacyNafUrl[];
};

const NestedItem = ({ item, legacyNafUrls }: Props) => {
  const { l10n } = useLocalization();
  const { featureEnabled } = useFeature();
  const listOfNafItems: {
    [key: string]: API.LinkMenuItem | API.AppMenuItem;
  } = entities.institution.useNAFItems();

  legacyNafUrls.forEach((link) => {
    listOfNafItems[link.title] = {
      url: link.url,
      openNewTab: link.open_new_tab,
    };
  });

  if (!item.itemProps.menu) {
    return null;
  }

  const translatedNestedMenu = translateTopMenuItems<API.AnyMenuItem>(
    Object.entries(item.itemProps.menu)
      .map(([name, itemProps]) => ({
        name,
        itemProps,
      }))
      .filter(({ itemProps }) => {
        if ("feature_flag" in itemProps && itemProps.feature_flag) {
          return featureEnabled({ or: itemProps.feature_flag });
        }
        return true;
      }),
    l10n
  );

  const renderedItems = translatedNestedMenu
    .reduce(
      (
        items: React.ReactElement<any>[],
        nestedMenuItem: RenderItemMenuProps<API.AnyMenuItem>
      ) => {
        let newItems: React.ReactElement<any>[] = [];
        if (isLinkItem(nestedMenuItem.itemProps)) {
          newItems = [
            <LinkItem
              item={{
                name: nestedMenuItem.name,
                itemProps: nestedMenuItem.itemProps,
              }}
              key={nestedMenuItem.name}
            />,
          ];
        }

        // This is a NAF app
        if (isAppItem(nestedMenuItem.itemProps)) {
          newItems = [
            <AppItem
              item={{
                name: nestedMenuItem.name,
                itemProps: nestedMenuItem.itemProps,
              }}
              key={nestedMenuItem.name}
            />,
          ];
        }

        // This item represents a list of _all_ NAF options, so we create
        // a list of individual Link and App items.
        // This ensures chunking layout logic can account for the number of NAF links
        if (isNafItem(nestedMenuItem.itemProps)) {
          newItems = Object.entries(listOfNafItems).map(
            ([nafName, nafItemProps]) => {
              if (isLinkItem(nafItemProps)) {
                return (
                  <LinkItem
                    item={{ name: nafName, itemProps: nafItemProps }}
                    key={nafName}
                  />
                );
              }
              if (isAppItem(nafItemProps)) {
                return (
                  <AppItem
                    item={{ name: nafName, itemProps: nafItemProps }}
                    key={nafName}
                  />
                );
              }
              return <></>;
            }
          );
        }

        if (isAoiobItem(nestedMenuItem.itemProps)) {
          newItems = [<AOIOBItem />];
        }

        return [...items, ...newItems];
      },
      []
    )
    .filter(Boolean);

  const unpackedItems: React.ReactElement<any>[] = renderedItems.flatMap(
    (renderedItem: React.ReactElement<any>) => {
      if (renderedItem && renderedItem.key !== "naf") {
        return renderedItem;
      }
      return renderedItem?.props.children;
    }
  );

  if (item.name === "Tools" && featureEnabled("positive_pay")) {
    unpackedItems.push(<PositivePayItem key="positivePay" />);
  }

  const chunkSize = unpackedItems.length % 5 !== 1 ? 5 : 6;

  const chunkedItems = utils.shardArrayByChunkSize(unpackedItems, chunkSize);

  // TODO: In the future, we may want to send this string already localized
  // in the respose via the `server` FTL product domain. @see `lib.fluent`.
  const translatedMenuName = item.name
    ? l10n.getString(
        new InstitutionFeatureFluentIds().getTopMenuItem(item.name),
        null,
        item.name
      )
    : item.name;

  return (
    <Details label={translatedMenuName} key={item.name} type="wide details">
      {chunkedItems.map((menuChunk, index) => (
        <div key={index} className="tools">
          {menuChunk}
        </div>
      ))}
    </Details>
  );
};

export default memo(NestedItem);
